import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  UserPlus,
  X,
  Save,
  Loader2,
  PowerOff,
  Power,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Input, Select, Button, Modal, Badge } from '../../Components/ui';
import { useConfirm, useToast } from '../../Components/Feedback';
import { AREAS, AREA_LABEL } from '../../utils/areas';
import { corCardNivel } from '../../utils/niveis';
import { API_URL } from '../../api/config';
import { useApi } from '../../api/useApi';

const DIAS_SEMANA = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
const HORARIOS = ['HR 1', 'HR 2', 'HR 3'];
const TURNOS = ['Manhã', 'Tarde', 'Noite'];
const DIA_ORDEM = DIAS_SEMANA.reduce((acc, dia, idx) => ({ ...acc, [dia]: idx }), {});

// Colunas clicáveis pra ordenar a tabela de turmas — cada uma sabe extrair seu
// próprio valor de comparação (ex.: dia da semana ordena pela semana, não por
// ordem alfabética, senão "Quinta" viria antes de "Segunda").
const COLUNAS_ORDENAVEIS = {
  nome: (t) => (t.nome || '').toLowerCase(),
  professor: (t) => (t.nome_professor || '').toLowerCase(),
  horario: (t) => [DIA_ORDEM[t.dia_semana] ?? 99, t.turno || '', t.horario || ''],
  area: (t) => AREA_LABEL[t.area] || t.area || '',
  alunos: (t) => Number(t.total_alunos) || 0,
  status: (t) => (t.data_fim ? 1 : 0),
};

// CRUD completo de turmas (criar, editar, apagar) + matricular/desmatricular
// alunos, tudo numa tela só. "Turma" aqui é uma linha de `atividades`
// (nome + professor + dia + horário + turno) — ver GradeTurmas.js e a
// conversa que motivou separar `atividades` por horário.
//
// Apagar é bloqueado pelo backend se a turma tiver qualquer matrícula
// vinculada (ativa OU histórica) — o front só exibe a mensagem de erro que
// vem da API nesse caso, não precisa duplicar a regra aqui.
function Turmas() {
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const { podeCriar, podeEditar, podeExcluir } = usePermissao('/turmas');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      'x-institution-id': institutionId,
      Authorization: `Bearer ${token}`,
    }),
    [institutionId, token],
  );
  const api = useApi();

  const [busca, setBusca] = useState('');
  const [filtroDia, setFiltroDia] = useState('');
  const [filtroHorario, setFiltroHorario] = useState('');
  const [filtroTurno, setFiltroTurno] = useState('');
  const [mostrarEncerradas, setMostrarEncerradas] = useState(false);
  const [ordenacao, setOrdenacao] = useState({ coluna: 'nome', direcao: 'asc' });

  const alternarOrdenacao = (coluna) => {
    setOrdenacao((prev) =>
      prev.coluna === coluna
        ? { coluna, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' }
        : { coluna, direcao: 'asc' },
    );
  };

  const { data: turmas = [], isLoading } = useQuery({
    queryKey: ['turmas', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/atividades`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
    // Essas turmas podem ter sido alteradas em outra tela (Ajuste de Grade,
    // Grade, Grade por Turma) que usa sua própria chave de cache —
    // refetchOnMount 'always' ignora o staleTime padrão (5min, ver App.js) e
    // busca de novo toda vez que essa tela é aberta, sem bloquear a exibição
    // do que já estava em cache.
    refetchOnMount: 'always',
    // Cobre o caso da tela já estar aberta numa aba em segundo plano enquanto
    // a alteração é feita em outra — refetchOnWindowFocus 'always' busca de
    // novo ao voltar o foco pra essa aba, sem esperar o staleTime.
    refetchOnWindowFocus: 'always',
  });

  const { data: professoresExistentes = [] } = useQuery({
    queryKey: ['professores-nomes', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/professores`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
  });

  const turmasFiltradas = useMemo(() => {
    const termo = busca.toLowerCase();
    const extrairValor = COLUNAS_ORDENAVEIS[ordenacao.coluna] || COLUNAS_ORDENAVEIS.nome;
    const sinal = ordenacao.direcao === 'asc' ? 1 : -1;

    return turmas
      .filter((t) => mostrarEncerradas || !t.data_fim)
      .filter(
        (t) =>
          !termo ||
          t.nome.toLowerCase().includes(termo) ||
          (t.nome_professor || '').toLowerCase().includes(termo),
      )
      .filter((t) => !filtroDia || t.dia_semana === filtroDia)
      .filter((t) => !filtroHorario || t.horario === filtroHorario)
      .filter((t) => !filtroTurno || t.turno === filtroTurno)
      .sort((a, b) => {
        const va = extrairValor(a);
        const vb = extrairValor(b);
        if (Array.isArray(va)) {
          for (let i = 0; i < va.length; i++) {
            if (va[i] < vb[i]) return -1 * sinal;
            if (va[i] > vb[i]) return 1 * sinal;
          }
          return 0;
        }
        if (va < vb) return -1 * sinal;
        if (va > vb) return 1 * sinal;
        return 0;
      });
  }, [turmas, busca, filtroDia, filtroHorario, filtroTurno, mostrarEncerradas, ordenacao]);

  const recarregarTurmas = () =>
    queryClient.invalidateQueries({ queryKey: ['turmas', institutionId] });

  // --- Criar / Editar turma ---
  const [modalAberto, setModalAberto] = useState(false);
  const [turmaEditando, setTurmaEditando] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    professor_nome: '',
    dia_semana: 'Segunda',
    horario: 'HR 1',
    turno: 'Manhã',
    area: 'educacional',
  });
  const [salvando, setSalvando] = useState(false);

  // Co-professores (co-docência) da turma em edição — só existe pra turma já
  // criada (precisa de um id pra vincular em atividade_professores), por isso
  // essa seção só aparece no modal quando `turmaEditando` existe.
  const [coProfessores, setCoProfessores] = useState([]);
  const [novoCoProfessor, setNovoCoProfessor] = useState('');
  const [salvandoCoProfessor, setSalvandoCoProfessor] = useState(false);

  const abrirNovaTurma = () => {
    setTurmaEditando(null);
    setForm({
      nome: '',
      professor_nome: '',
      dia_semana: 'Segunda',
      horario: 'HR 1',
      turno: 'Manhã',
      area: 'educacional',
    });
    setCoProfessores([]);
    setNovoCoProfessor('');
    setModalAberto(true);
  };

  const abrirEditarTurma = (turma) => {
    setTurmaEditando(turma);
    setForm({
      nome: turma.nome,
      professor_nome: turma.nome_professor || '',
      dia_semana: turma.dia_semana || 'Segunda',
      horario: turma.horario || 'HR 1',
      turno: turma.turno || 'Manhã',
      area: turma.area || 'educacional',
    });
    setCoProfessores(turma.professores_adicionais || []);
    setNovoCoProfessor('');
    setModalAberto(true);
  };

  const adicionarCoProfessor = async () => {
    const nome = novoCoProfessor.trim();
    if (!nome || !turmaEditando) return;
    setSalvandoCoProfessor(true);
    try {
      const data = await api.post(`/api/atividades/${turmaEditando.id}/professores`, {
        professor_nome: nome,
      });
      setCoProfessores((prev) => [...prev, data?.data ?? data]);
      setNovoCoProfessor('');
      recarregarTurmas();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvandoCoProfessor(false);
    }
  };

  const removerCoProfessor = async (idprofessor) => {
    try {
      const res = await fetch(
        `${API_URL}/api/atividades/${turmaEditando.id}/professores/${idprofessor}`,
        { method: 'DELETE', headers: authHeaders },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao remover educador.');
      setCoProfessores((prev) => prev.filter((p) => p.id !== idprofessor));
      recarregarTurmas();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const salvarTurma = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      const url = turmaEditando
        ? `${API_URL}/api/atividades/${turmaEditando.id}`
        : `${API_URL}/api/atividades`;
      const method = turmaEditando ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar turma');
      toast.success(turmaEditando ? 'Turma atualizada com sucesso.' : 'Turma criada com sucesso.');
      setModalAberto(false);
      recarregarTurmas();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  // --- Encerrar / reabrir turma (soft-close: mantém a turma e o histórico,
  // só marca data_fim e desmatricula quem estava ativo nela) ---
  // Atualiza a turma já em cache sem esperar o round-trip de recarregarTurmas
  // (invalidateQueries) — é essa espera que fazia a turma demorar pra
  // sair/entrar na lista depois de encerrar/reabrir/apagar.
  const patchTurmaEmCache = (id, patch) => {
    queryClient.setQueryData(['turmas', institutionId], (old) =>
      Array.isArray(old) ? old.map((t) => (t.id === id ? { ...t, ...patch } : t)) : old,
    );
  };
  const removerTurmaEmCache = (id) => {
    queryClient.setQueryData(['turmas', institutionId], (old) =>
      Array.isArray(old) ? old.filter((t) => t.id !== id) : old,
    );
  };

  const encerrarTurma = async (turma) => {
    const aviso =
      turma.total_alunos > 0
        ? `Encerrar "${turma.nome}"? Os ${turma.total_alunos} inscrito(s) ativos dela serão desmatriculados (o histórico continua registrado).`
        : `Encerrar "${turma.nome}"?`;
    if (
      !(await confirm(aviso, { title: 'Encerrar turma', confirmText: 'Encerrar', tone: 'danger' }))
    )
      return;
    try {
      const data = await api.post(`/api/atividades/${turma.id}/encerrar`);
      patchTurmaEmCache(turma.id, {
        data_fim: new Date().toISOString().split('T')[0],
        total_alunos: 0,
      });
      const desmatriculados = data?.data?.alunos_desmatriculados ?? data?.alunos_desmatriculados ?? 0;
      toast.success(
        `Turma encerrada${desmatriculados > 0 ? ` — ${desmatriculados} inscrito(s) desmatriculado(s)` : ''}.`,
      );
      recarregarTurmas();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const reabrirTurma = async (turma) => {
    try {
      await api.post(`/api/atividades/${turma.id}/reabrir`);
      patchTurmaEmCache(turma.id, { data_fim: null });
      toast.success('Turma reaberta com sucesso. Matricule os inscritos novamente se for o caso.');
      recarregarTurmas();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // --- Apagar turma ---
  const apagarTurma = async (turma) => {
    const ok = await confirm(
      `Apagar a turma "${turma.nome}" (${turma.dia_semana || '-'} ${turma.horario || ''}, ${turma.turno || ''})?`,
      { title: 'Apagar turma', confirmText: 'Apagar' },
    );
    if (!ok) return;
    try {
      await api.del(`/api/atividades/${turma.id}`);
      removerTurmaEmCache(turma.id);
      toast.success('Turma apagada com sucesso.');
      recarregarTurmas();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // --- Matricular / desmatricular alunos ---
  const [modalMatricularAberto, setModalMatricularAberto] = useState(false);
  const [turmaParaMatricular, setTurmaParaMatricular] = useState(null);
  const [buscaAluno, setBuscaAluno] = useState('');
  const [buscaAlunoDebounced, setBuscaAlunoDebounced] = useState('');
  const [processandoMatricula, setProcessandoMatricula] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setBuscaAlunoDebounced(buscaAluno), 300);
    return () => clearTimeout(timer);
  }, [buscaAluno]);

  const { data: matriculadosDaTurma = [], isLoading: carregandoMatriculados } = useQuery({
    queryKey: ['matriculados-turma', turmaParaMatricular?.id],
    queryFn: async () => {
      const res = await api.get(
        `/api/matriculas/por-instituicao?id_atividade=${turmaParaMatricular.id}`,
      );
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: modalMatricularAberto && !!turmaParaMatricular,
    refetchOnMount: 'always', // sempre atualizado ao abrir o card da turma, não só na primeira vez
    refetchOnWindowFocus: 'always',
  });

  const { data: resultadosBuscaAluno = [] } = useQuery({
    queryKey: ['busca-aluno-matricular', institutionId, buscaAlunoDebounced],
    queryFn: async () => {
      const res = await api.get(
        `/api/alunos?nome=${encodeURIComponent(buscaAlunoDebounced)}`,
      );
      const listaAlunos = res?.data ?? (Array.isArray(res) ? res : []);
      const listaMatriculados = Array.isArray(matriculadosDaTurma) ? matriculadosDaTurma : [];
      const idsJaMatriculados = new Set(listaMatriculados.map((m) => m.aluno_id));
      return listaAlunos.filter((a) => !idsJaMatriculados.has(a.id));
    },
    enabled: !!buscaAlunoDebounced && modalMatricularAberto,
  });

  const abrirMatricular = (turma) => {
    setTurmaParaMatricular(turma);
    setBuscaAluno('');
    setModalMatricularAberto(true);
  };

  const recarregarMatriculados = () => {
    queryClient.invalidateQueries({ queryKey: ['matriculados-turma', turmaParaMatricular?.id] });
    recarregarTurmas(); // atualiza a contagem de alunos na lista principal também
  };

  const adicionarAluno = async (aluno) => {
    setProcessandoMatricula(true);
    try {
      await api.post(`/api/matriculas/matricular`, {
        aluno_id: aluno.id,
        id_atividade: turmaParaMatricular.id,
      });
      setBuscaAluno('');
      recarregarMatriculados();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessandoMatricula(false);
    }
  };

  const removerAluno = async (matricula) => {
    if (
      !(await confirm(`Remover ${matricula.nome_aluno} dessa turma?`, {
        title: 'Remover inscrito',
        confirmText: 'Remover',
      }))
    )
      return;
    setProcessandoMatricula(true);
    try {
      await api.del(`/api/matriculas/${matricula.id}`);
      recarregarMatriculados();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessandoMatricula(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-6xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full sm:w-80">
              <Input
                placeholder="Buscar turma ou educador..."
                icon={Search}
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
            <div className="w-32">
              <Select
                size="sm"
                value={filtroDia}
                onChange={(e) => setFiltroDia(e.target.value)}
                options={[
                  { value: '', label: 'Todo dia' },
                  ...DIAS_SEMANA.map((d) => ({ value: d, label: d })),
                ]}
              />
            </div>
            <div className="w-28">
              <Select
                size="sm"
                value={filtroHorario}
                onChange={(e) => setFiltroHorario(e.target.value)}
                options={[
                  { value: '', label: 'Todo horário' },
                  ...HORARIOS.map((h) => ({ value: h, label: h })),
                ]}
              />
            </div>
            <div className="w-32">
              <Select
                size="sm"
                value={filtroTurno}
                onChange={(e) => setFiltroTurno(e.target.value)}
                options={[
                  { value: '', label: 'Todo turno' },
                  ...TURNOS.map((t) => ({ value: t, label: t })),
                ]}
              />
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={mostrarEncerradas}
                onChange={(e) => setMostrarEncerradas(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              Mostrar encerradas
            </label>
          </div>
          {podeCriar && (
            <Button variant="primary" icon={Plus} onClick={abrirNovaTurma}>
              Nova Turma
            </Button>
          )}
        </div>

        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/60">
                <tr>
                  <ThOrdenavel
                    coluna="nome"
                    label="Turma"
                    ordenacao={ordenacao}
                    onSort={alternarOrdenacao}
                  />
                  <ThOrdenavel
                    coluna="professor"
                    label="Educador"
                    ordenacao={ordenacao}
                    onSort={alternarOrdenacao}
                  />
                  <ThOrdenavel
                    coluna="horario"
                    label="Horário"
                    ordenacao={ordenacao}
                    onSort={alternarOrdenacao}
                  />
                  <ThOrdenavel
                    coluna="area"
                    label="Área"
                    ordenacao={ordenacao}
                    onSort={alternarOrdenacao}
                  />
                  <ThOrdenavel
                    coluna="alunos"
                    label="Inscritos"
                    align="center"
                    ordenacao={ordenacao}
                    onSort={alternarOrdenacao}
                  />
                  <ThOrdenavel
                    coluna="status"
                    label="Status"
                    align="center"
                    ordenacao={ordenacao}
                    onSort={alternarOrdenacao}
                  />
                  <th className="px-4 py-2.5 text-right font-medium text-gray-500 dark:text-gray-400 text-xs">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-gray-500 dark:text-gray-400"
                    >
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                      Carregando turmas...
                    </td>
                  </tr>
                ) : turmasFiltradas.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-gray-400 dark:text-gray-500 text-sm"
                    >
                      Nenhuma turma encontrada.
                    </td>
                  </tr>
                ) : (
                  turmasFiltradas.map((turma) => {
                    const encerrada = !!turma.data_fim;
                    return (
                      <tr
                        key={turma.id}
                        className={`hover:bg-gray-50/50 dark:hover:bg-gray-800/40 ${encerrada ? 'opacity-60' : ''}`}
                      >
                        <td
                          className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100"
                          translate="no"
                        >
                          {turma.nome}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400" translate="no">
                          {turma.nome_professor || (
                            <span className="text-gray-300 dark:text-gray-700">—</span>
                          )}
                          {turma.professores_adicionais?.length > 0 && (
                            <span
                              title={turma.professores_adicionais.map((p) => p.nome).join(', ')}
                              className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-[10px] font-medium"
                            >
                              +{turma.professores_adicionais.length}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">
                          {turma.dia_semana && turma.horario ? (
                            `${turma.dia_semana} · ${turma.horario} · ${turma.turno}`
                          ) : (
                            <span className="text-gray-300 dark:text-gray-700">sem horário</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          {turma.area ? (
                            <Badge tone="neutral">{AREA_LABEL[turma.area] || turma.area}</Badge>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-700">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <Badge tone={turma.total_alunos > 0 ? 'info' : 'neutral'}>
                            {turma.total_alunos}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <Badge tone={encerrada ? 'neutral' : 'success'}>
                            {encerrada ? 'Encerrada' : 'Ativa'}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            {podeEditar && (
                              <button
                                onClick={() => abrirMatricular(turma)}
                                title={
                                  encerrada
                                    ? 'Turma encerrada — reabra para matricular'
                                    : 'Matricular inscritos'
                                }
                                disabled={encerrada}
                                className="w-8 h-8 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                              >
                                <UserPlus className="w-4 h-4" />
                              </button>
                            )}
                            {podeEditar && (
                              <button
                                onClick={() => abrirEditarTurma(turma)}
                                title="Editar turma"
                                className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            {podeEditar &&
                              (encerrada ? (
                                <button
                                  onClick={() => reabrirTurma(turma)}
                                  title="Reabrir turma"
                                  className="w-8 h-8 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/30 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                                >
                                  <Power className="w-4 h-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => encerrarTurma(turma)}
                                  title="Encerrar turma (mantém o histórico)"
                                  className="w-8 h-8 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/30 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                                >
                                  <PowerOff className="w-4 h-4" />
                                </button>
                              ))}
                            {podeExcluir && (
                              <button
                                onClick={() => apagarTurma(turma)}
                                title="Apagar turma"
                                className="w-8 h-8 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </main>

      {/* Modal: Criar / Editar turma */}
      <Modal
        isOpen={modalAberto}
        onClose={() => setModalAberto(false)}
        title={turmaEditando ? 'Editar Turma' : 'Nova Turma'}
        maxWidth="md"
      >
        <form onSubmit={salvarTurma} className="space-y-4">
          <Input
            label="Nome da turma"
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
            required
            autoFocus
          />

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Educador
            </label>
            <input
              list="lista-professores-turmas"
              value={form.professor_nome}
              onChange={(e) => setForm({ ...form, professor_nome: e.target.value })}
              placeholder="Nome do educador (existente ou novo)"
              className="w-full h-10 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
            <datalist id="lista-professores-turmas">
              {professoresExistentes.map((nome) => (
                <option key={nome} value={nome} />
              ))}
            </datalist>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Select
              label="Dia"
              value={form.dia_semana}
              onChange={(e) => setForm({ ...form, dia_semana: e.target.value })}
              options={DIAS_SEMANA.map((d) => ({ value: d, label: d }))}
            />
            <Select
              label="Horário"
              value={form.horario}
              onChange={(e) => setForm({ ...form, horario: e.target.value })}
              options={HORARIOS.map((h) => ({ value: h, label: h }))}
            />
            <Select
              label="Turno"
              value={form.turno}
              onChange={(e) => setForm({ ...form, turno: e.target.value })}
              options={TURNOS.map((t) => ({ value: t, label: t }))}
            />
          </div>

          <Select
            label="Área"
            value={form.area}
            onChange={(e) => setForm({ ...form, area: e.target.value })}
            options={AREAS.map((a) => ({ value: a.value, label: a.label }))}
          />

          {turmaEditando && (
            <div className="space-y-2 pt-1">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                Outros educadores nesta turma (co-docência)
              </label>
              {coProfessores.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {coProfessores.map((p) => (
                    <span
                      key={p.id}
                      className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs"
                      translate="no"
                    >
                      {p.nome}
                      <button
                        type="button"
                        onClick={() => removerCoProfessor(p.id)}
                        className="w-4 h-4 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/50 flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  list="lista-professores-turmas"
                  value={novoCoProfessor}
                  onChange={(e) => setNovoCoProfessor(e.target.value)}
                  placeholder="Adicionar outro educador..."
                  className="flex-1 h-9 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!novoCoProfessor.trim() || salvandoCoProfessor}
                  onClick={adicionarCoProfessor}
                >
                  Adicionar
                </Button>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Qualquer educador listado aqui pode bater ponto e lançar nota dessa turma, igual ao
                principal.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalAberto(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" icon={Save} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal: Matricular inscritos */}
      <Modal
        isOpen={modalMatricularAberto}
        onClose={() => setModalMatricularAberto(false)}
        title={turmaParaMatricular ? `Matricular — ${turmaParaMatricular.nome}` : 'Matricular'}
        maxWidth="lg"
      >
        {turmaParaMatricular && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 rounded-xl p-2.5 border border-gray-100 dark:border-gray-800">
              <span translate="no">{turmaParaMatricular.nome_professor || 'Sem educador'}</span>
              <span>·</span>
              <span>
                {turmaParaMatricular.dia_semana} {turmaParaMatricular.horario} (
                {turmaParaMatricular.turno})
              </span>
            </div>

            <div className="relative">
              <Input
                placeholder="Buscar inscrito pelo nome para adicionar..."
                icon={Search}
                value={buscaAluno}
                onChange={(e) => setBuscaAluno(e.target.value)}
                disabled={processandoMatricula}
              />
              {buscaAlunoDebounced && resultadosBuscaAluno.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-auto">
                  {resultadosBuscaAluno.map((aluno) => (
                    <button
                      key={aluno.id}
                      type="button"
                      onClick={() => adicionarAluno(aluno)}
                      disabled={processandoMatricula}
                      className="w-full flex items-center justify-between px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 disabled:opacity-50"
                    >
                      <span translate="no">{aluno.nome}</span>
                      <Plus className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Inscritos matriculados ({Array.isArray(matriculadosDaTurma) ? matriculadosDaTurma.length : 0})
              </p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {carregandoMatriculados ? (
                  <div className="flex items-center justify-center py-6 text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                ) : !Array.isArray(matriculadosDaTurma) || matriculadosDaTurma.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                    Nenhum inscrito matriculado ainda.
                  </p>
                ) : (
                  matriculadosDaTurma.map((m) => (
                    <div
                      key={m.id}
                      className={`flex items-center justify-between px-3 py-2 rounded-xl border border-gray-100 dark:border-gray-800 ${corCardNivel(m.nivel) || 'bg-gray-50/60 dark:bg-gray-800/40'}`}
                    >
                      <span className="text-sm text-gray-900 dark:text-gray-100" translate="no">
                        {m.nome_aluno}
                      </span>
                      <button
                        onClick={() => removerAluno(m)}
                        disabled={processandoMatricula}
                        className="w-7 h-7 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setModalMatricularAberto(false)}
              >
                Fechar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// Cabeçalho de coluna clicável: clica pra ordenar por ela, clica de novo pra
// inverter a direção. A seta mostra a direção ativa; nas outras colunas fica
// uma seta dupla neutra só visível no hover, pra dar a dica sem poluir.
const ALINHAMENTO_TEXTO = { left: 'text-left', center: 'text-center', right: 'text-right' };
const ALINHAMENTO_FLEX = { left: 'justify-start', center: 'justify-center', right: 'justify-end' };

function ThOrdenavel({ coluna, label, ordenacao, onSort, align = 'left' }) {
  const ativa = ordenacao.coluna === coluna;
  const Icone = ativa ? (ordenacao.direcao === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <th
      className={`px-4 py-2.5 ${ALINHAMENTO_TEXTO[align]} font-medium text-gray-500 dark:text-gray-400 text-xs`}
    >
      <button
        onClick={() => onSort(coluna)}
        className={`inline-flex items-center gap-1 ${ALINHAMENTO_FLEX[align]} w-full hover:text-gray-900 dark:hover:text-gray-100 transition-colors group`}
      >
        {label}
        <Icone
          className={`w-3.5 h-3.5 ${ativa ? 'text-blue-600 dark:text-blue-400' : 'text-gray-300 dark:text-gray-700 group-hover:text-gray-400 dark:group-hover:text-gray-500'}`}
        />
      </button>
    </th>
  );
}

export default Turmas;
